import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Warning from "@mui/icons-material/Warning";
import { GridColDef } from "@mui/x-data-grid";
import { abbreviateNumber, formatNumber } from "../../../../utils/numberUtils";
import { formatRevenueRange } from "./utils";
import { TableRow, DataRow } from "./types";
import { CompanyNameCell } from "./components/CompanyNameCell";
import { CompanyTypeCell } from "./components/CompanyTypeCell";
import { CompetitorPresenceCell } from "./components/CompetitorPresenceCell";
import WorkSpaceStyledTooltip from "../../Header/SelectedMarketFilters/WorkSpaceStyledTooltip";
import { InsufficientInfoWrapper } from "../CompetitiveAnalysis/InsufficientInfoWrapper";

const getTitleWithInsufficientInfo = (
  title: string,
  hasData: boolean,
  isForWhitespace = false
) => {
  return (
    <Box gap={1} display="flex" alignItems="center">
      {title}
      {!hasData ? (
        <WorkSpaceStyledTooltip
          arrow
          placement="bottom-end"
          title={
            <InsufficientInfoWrapper
              isForTable
              isForWhitespace={isForWhitespace}
            />
          }
          sx={{
            "& .MuiTooltip-tooltip": { minWidth: "375px" },
          }}
        >
          <Warning
            htmlColor="#ffa726"
            sx={{
              fontSize: "16px",
            }}
          />
        </WorkSpaceStyledTooltip>
      ) : null}
    </Box>
  );
};

export const getBaseColumns = (): GridColDef<TableRow>[] => [
  {
    field: "name",
    headerName: "Company Name",
    minWidth: 200,
    flex: 1,
    sortable: true,
    renderCell: CompanyNameCell,
  },
  {
    field: "url",
    headerName: "URL",
    width: 100,
    sortable: true,
    renderCell: (params) => {
      if (params.row.isSkeleton) {
        return <Skeleton variant="text" width="80%" />;
      }
      const rowData = params.row as DataRow;
      return (
        <Tooltip title={rowData.url || ""} placement="bottom">
          <Typography variant="body2" noWrap>
            {rowData.url || ""}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "hierarchy",
    headerName: "Company Type",
    sortable: false,
    width: 200,
    renderCell: CompanyTypeCell,
  },
  {
    field: "country_name",
    headerName: "Country",
    sortable: true,
    minWidth: 200,
    flex: 1,
    renderCell: (params) => {
      if (params.row.isSkeleton) {
        return <Skeleton variant="text" width="70%" />;
      }
      const rowData = params.row as DataRow;
      return (
        <Tooltip title={rowData.countryName || ""} placement="bottom">
          <Typography variant="body2" noWrap>
            {rowData.countryName || ""}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "employee_range_min",
    headerName: "Employees",
    width: 130,
    sortable: true,
    renderCell: (params) => {
      if (params.row.isSkeleton) {
        return (
          <Skeleton
            variant="rounded"
            width={64}
            height={22}
            sx={{ borderRadius: "16px" }}
          />
        );
      }
      const rowData = params.row as DataRow;
      const tooltipValue = rowData.employeeRange?.max
        ? formatNumber(rowData.employeeRange.max)
        : "";
      return rowData.employeeRange?.max ? (
        <Tooltip title={tooltipValue} placement="bottom">
          <Chip
            label={abbreviateNumber(rowData.employeeRange.max)}
            size="medium"
            sx={{
              bgcolor: "grey.200",
              width: 64,
            }}
          />
        </Tooltip>
      ) : null;
    },
  },
  {
    field: "revenue_range_min",
    headerName: "Revenue",
    width: 150,
    sortable: true,
    renderCell: (params) => {
      if (params.row.isSkeleton) {
        return <Skeleton variant="text" width="70%" />;
      }
      const rowData = params.row as DataRow;
      const displayValue = formatRevenueRange({
        min: rowData.revenueRange?.min ?? undefined,
        max: rowData.revenueRange?.max ?? undefined,
      });
      const tooltipValue = rowData.revenueRange?.max
        ? formatNumber(rowData.revenueRange.max)
        : displayValue;
      return (
        <Tooltip title={tooltipValue} placement="bottom">
          <Typography variant="body2">{displayValue}</Typography>
        </Tooltip>
      );
    },
  },
  {
    field: "it_spend",
    headerName: "IT Spend",
    width: 130,
    sortable: true,
    renderCell: (params) => {
      if (params.row.isSkeleton) {
        return <Skeleton variant="text" width="60%" />;
      }
      const rowData = params.row as DataRow;
      const displayValue = rowData.itSpend
        ? abbreviateNumber(rowData.itSpend)
        : "";
      const tooltipValue = rowData.itSpend ? formatNumber(rowData.itSpend) : "";
      return (
        <Tooltip title={tooltipValue} placement="bottom">
          <Typography variant="body2">{displayValue}</Typography>
        </Tooltip>
      );
    },
  },
];

export const getCompetitorPresenceColumn = (
  competitorProductMetadata: Record<number, string>,
  hasCompetitorPresence: boolean
): GridColDef<TableRow> => ({
  field: "competitor_presence",
  headerName: "Competitor Presence",
  width: 200,
  sortable: false,
  align: "center",
  headerAlign: "center",
  renderHeader: () =>
    getTitleWithInsufficientInfo("Competitor Presence", hasCompetitorPresence),
  renderCell: (params) => {
    return (
      <CompetitorPresenceCell
        {...params}
        competitorProductMetadata={competitorProductMetadata}
        hasCompetitorPresence={hasCompetitorPresence}
      />
    );
  },
});

export const getOpportunityTypeColumn = (
  hasMatchFile: boolean,
  enableHierarchyClassification: boolean = false
): GridColDef<TableRow> => ({
  field: "opportunity_type",
  headerName: "Opportunity Type",
  width: 180,
  align: "center",
  headerAlign: "center",
  sortable: false,
  renderHeader: () =>
    getTitleWithInsufficientInfo("Opportunity Type", hasMatchFile, true),
  renderCell: (params) => {
    if (params.row.isSkeleton) {
      return (
        <Skeleton
          variant="rounded"
          width={100}
          height={22}
          sx={{ borderRadius: "16px" }}
        />
      );
    }
    const rowData = params.row as DataRow;

    let label: string;
    let backgroundColor: string;
    let textColor = "#343C43DE";

    if (!enableHierarchyClassification) {
      if (rowData.isWhitespace) {
        label = "Whitespace";
        backgroundColor = "#e3f2fd"; // Light blue
      } else {
        label = "Customer";
        backgroundColor = "#e8f5e8"; // Light green
      }
    } else {
      if (!rowData.isWhitespace) {
        label = "Customer";
        backgroundColor = "#e8f5e8"; // Light green
      } else if (rowData.isExpansionWhitespace) {
        label = "Expansion Whitespace";
        backgroundColor = "#7e57c2"; // Dark purple
        textColor = "white";
      } else if (rowData.isProspectWhitespace) {
        label = "Prospect Whitespace";
        backgroundColor = "#e1bee7"; // Light purple
      } else {
        label = "Whitespace";
        backgroundColor = "#e3f2fd"; // Light blue
      }
    }

    return (
      <Chip
        label={label}
        size="medium"
        sx={{
          backgroundColor,
          color: textColor,
          minWidth: 120,
          "& .MuiChip-label": {
            fontWeight: "normal",
            fontSize: "12px",
          },
        }}
      />
    );
  },
});
